// The order of the master list (module 10 U80, U92). Pure, tested, and the only place that decides what
// "most pressing" means.
//
// Mike, reading his own list on 2026-09-20: "it is sort by priority, but first 3 are … was due Sep 18 2025 …
// These are the 3 most important and urgent things from 2025 ?????? Utter dog shit." The old key was
// [overdue ? 0 : 1, priority, dueAt], so overdue by a day and overdue by a year sorted the same and the
// oldest date won inside the bucket. Measured on his store the same afternoon: 121 rows, 104 of them more
// than 90 days past their date, the middle row 270 days past, and every single one priority 3.
//
// What he chose instead: the freshest slip on top. A thing missed last week is pressing; a thing missed last
// year is evidence that nobody closed it, and it leaves the list under one folded line.
import { dueKind } from './when.js';

const DAY = 86_400_000;
const startOfLocalDay = (t: number): number => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime(); };

/** Days between a due date and today, counted in whole days of the person's own calendar; 0 is today. */
export function daysPast(iso: string, now: number = Date.now()): number {
  return Math.round((startOfLocalDay(now) - startOfLocalDay(new Date(iso).getTime())) / DAY);
}

/** More than this many days past its date and a task leaves the list into the folded group (Mike, 2026-09-20). */
export const LONG_OVERDUE_DAYS = 30;

/**
 * Which band a row falls in. `missed` leads the page, then `today`, then `week`, then `later`; `long` is the
 * folded group at the foot and never mixes with the rest.
 */
export type Band = 'missed' | 'today' | 'week' | 'later' | 'long';
export const BANDS: readonly Band[] = ['missed', 'today', 'week', 'later'] as const;

export function bandOf(dueAt: string, duePrecision: string, now: number = Date.now()): Band {
  const kind = dueKind(dueAt, duePrecision, now);
  if (kind !== 'overdue') return kind;
  return daysPast(dueAt, now) > LONG_OVERDUE_DAYS ? 'long' : 'missed';
}

/** What the order needs of a row, whether it is a task the person accepted or a proposal of Cobrain's. */
export interface Orderable { dueAt: string; duePrecision: string; priority: number; proposed: boolean }

/**
 * The comparison, given both rows are in the same band. Missed and long-overdue run newest first (the freshest
 * slip on top); everything ahead runs soonest first. Inside one day, priority decides, and then a task the
 * person accepted sits above a proposal of the same weight — Mike's "under yours on a tie", which keeps a
 * proposal in its place in the order rather than in a pile of its own.
 */
function within(band: Band, a: Orderable, b: Orderable): number {
  const back = band === 'missed' || band === 'long' ? -1 : 1;
  const dayA = startOfLocalDay(new Date(a.dueAt).getTime()), dayB = startOfLocalDay(new Date(b.dueAt).getTime());
  if (dayA !== dayB) return back * (dayA - dayB);
  if (a.priority !== b.priority) return a.priority - b.priority;
  if (a.proposed !== b.proposed) return a.proposed ? 1 : -1;
  return back * (new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime());
}

/** Rows in the order the master list draws them, and the folded long-overdue group kept apart from them. */
export function masterOrder<T extends Orderable>(rows: readonly T[], now: number = Date.now()): { list: T[]; long: T[] } {
  const banded = rows.map((r) => ({ r, band: bandOf(r.dueAt, r.duePrecision, now) }));
  const list = banded.filter((x) => x.band !== 'long')
    .sort((x, y) => BANDS.indexOf(x.band) - BANDS.indexOf(y.band) || within(x.band, x.r, y.r))
    .map((x) => x.r);
  const long = banded.filter((x) => x.band === 'long').sort((x, y) => within('long', x.r, y.r)).map((x) => x.r);
  return { list, long };
}
